import { Link } from 'react-router-dom'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { useSession } from '@/client/lib/auth'
import { useBalance } from '@/client/modules/credits/hooks/useCredits'
import { useOrders } from '@/client/modules/orders/hooks/useOrders'
import { useAddresses } from '@/client/modules/addresses/hooks/useAddresses'
import { Mail, Package, Wallet, ArrowRight, BookUser, FileText, CheckCircle2 } from 'lucide-react'

const statusColour: Record<string, string> = {
  pending: 'bg-yellow-500/10 text-yellow-600',
  awaiting_payment: 'bg-orange-500/10 text-orange-600',
  processing: 'bg-blue-500/10 text-blue-600',
  fulfilling: 'bg-purple-500/10 text-purple-600',
  completed: 'bg-green-500/10 text-green-600',
  delivered: 'bg-green-700/10 text-green-700',
}

export function DashboardPage() {
  const { data: session } = useSession()
  const { data: balanceData, isLoading: balanceLoading } = useBalance()
  const { data: ordersData, isLoading: ordersLoading } = useOrders()
  const { data: addressData } = useAddresses()

  const orders = ordersData?.orders ?? []
  const recentOrders = orders.slice(0, 5)
  const addressCount = addressData?.addresses?.length ?? 0
  const isNewUser = orders.length === 0 && addressCount === 0 && !ordersLoading

  return (
    <div className="space-y-6">
      {/* Welcome */}
      <div className="space-y-2">
        <h1 className="text-3xl font-bold tracking-tight">
          Welcome{session?.user?.name ? `, ${session.user.name.split(' ')[0]}` : ''}!
        </h1>
        <p className="text-muted-foreground">
          Send physical mail from your browser. Here is your overview.
        </p>
      </div>

      {/* Stats Row */}
      <div className="grid gap-4 md:grid-cols-3">
        {/* Balance */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Balance</CardTitle>
            <Wallet className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {balanceLoading ? (
              <Skeleton className="h-8 w-24" />
            ) : (
              <p className="text-2xl font-bold font-mono">
                {balanceData?.balance ?? '$0.00'}
              </p>
            )}
          </CardContent>
        </Card>

        {/* Total Orders */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">Total Orders</CardTitle>
            <Package className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            {ordersLoading ? (
              <Skeleton className="h-8 w-16" />
            ) : (
              <p className="text-2xl font-bold">{orders.length}</p>
            )}
          </CardContent>
        </Card>

        {/* Quick Action */}
        <Card className="flex flex-col justify-center">
          <CardContent className="pt-6">
            <Button asChild className="w-full" size="lg">
              <Link to="/dashboard/send/letter">
                <Mail className="mr-2 h-4 w-4" />
                Send a Letter
              </Link>
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Getting Started — shown for new users */}
      {isNewUser && (
        <Card className="border-primary/30">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <CheckCircle2 className="h-4 w-4 text-primary" />
              Getting Started
            </CardTitle>
            <CardDescription>Complete these steps to send your first letter.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <Link to="/dashboard/addresses" className="flex items-center gap-3 rounded-lg border p-3 hover:bg-accent transition-colors">
                <BookUser className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">Add a return address</p>
                  <p className="text-xs text-muted-foreground">Save your address for quick reuse</p>
                </div>
              </Link>
              <Link to="/dashboard/templates" className="flex items-center gap-3 rounded-lg border p-3 hover:bg-accent transition-colors">
                <FileText className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">Browse templates</p>
                  <p className="text-xs text-muted-foreground">Choose from our template library</p>
                </div>
              </Link>
              <Link to="/dashboard/send/letter" className="flex items-center gap-3 rounded-lg border p-3 hover:bg-accent transition-colors">
                <Mail className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">Send your first letter</p>
                  <p className="text-xs text-muted-foreground">Print and post a letter anywhere in Australia</p>
                </div>
              </Link>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick Actions */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Mail className="h-4 w-4 text-primary" />
              Send Letter
            </CardTitle>
            <CardDescription>Print and post a letter anywhere in Australia.</CardDescription>
          </CardHeader>
          <CardContent>
            <Button asChild variant="outline" size="sm">
              <Link to="/dashboard/send/letter">Get Started</Link>
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Package className="h-4 w-4 text-primary" />
              View Orders
            </CardTitle>
            <CardDescription>Track and manage your order history.</CardDescription>
          </CardHeader>
          <CardContent>
            <Button asChild variant="outline" size="sm">
              <Link to="/dashboard/orders">View All</Link>
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Wallet className="h-4 w-4 text-primary" />
              Top Up Balance
            </CardTitle>
            <CardDescription>Add funds to your account for sending mail.</CardDescription>
          </CardHeader>
          <CardContent>
            <Button asChild variant="outline" size="sm">
              <Link to="/dashboard/account">Manage Account</Link>
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Recent Orders */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Recent Orders</CardTitle>
          {orders.length > 0 && (
            <Button asChild variant="ghost" size="sm">
              <Link to="/dashboard/orders">
                View All <ArrowRight className="ml-1 h-3 w-3" />
              </Link>
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {ordersLoading ? (
            <div className="space-y-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : recentOrders.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">
              No orders yet. <Link to="/dashboard/send/letter" className="text-primary hover:underline">Send your first letter</Link>.
            </p>
          ) : (
            <div className="space-y-2">
              {recentOrders.map((order) => (
                <Link
                  key={order.id}
                  to={`/dashboard/orders/${order.id}`}
                  className="flex items-center justify-between rounded-lg border p-3 hover:bg-accent transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-sm">{order.id.slice(0, 12)}...</span>
                    <Badge variant="secondary" className={statusColour[order.status] ?? ''}>
                      {order.status}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-sm font-mono">${(order.costCents / 100).toFixed(2)}</span>
                    <span className="text-xs text-muted-foreground">
                      {new Date(order.createdAt).toLocaleDateString('en-AU')}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
